/**
 * Host (Telegram / native shell) chrome sits over the top of fullscreen surfaces.
 * Titles, icons, and leading/trailing actions must clear:
 *  - Left: Telegram ← / Close and in-app floating back
 *  - Right: Telegram expand ▾ and ⋯ menu
 *
 * Those gutters only apply when `body` has HOST_CHROME_BODY_CLASS (Telegram Mini
 * App). On the public website there is no host chrome, so titles sit on the far
 * left and close controls sit on the far right.
 *
 * Use:
 * - Global theme CSS covers most MUI DialogTitle rows in fullScreen dialogs
 * - `hostBackSafeHeaderSx` / `data-host-back-header` for custom sticky headers
 * - `data-host-back-centered="true"` to opt into a centered title layout that
 *   already reserves its own left gutter (e.g. Choose a Chip)
 */

import type { SxProps, Theme } from '@mui/material/styles';

/** Applied to <body> when Telegram (or another host) owns overlay chrome. */
export const HOST_CHROME_BODY_CLASS = 'zwn-host-chrome';

/** Home-screen / fullscreen Mini App: Telegram’s title bar paints on top of the WebView. */
export const HOST_OVERLAY_BODY_CLASS = 'zwn-host-overlay';

/** Approximate hit area of the host back control + breathing room */
export const HOST_BACK_GUTTER_PX = 76;

/** Telegram expand + more-menu cluster on the trailing edge */
export const HOST_TG_TRAILING_GUTTER_PX = 72;

export const HOST_BACK_SAFE_PADDING_LEFT =
  `max(16px, calc(env(safe-area-inset-left, 0px) + ${HOST_BACK_GUTTER_PX}px))`;

export const HOST_BACK_SAFE_PADDING_RIGHT =
  `max(16px, calc(env(safe-area-inset-right, 0px) + ${HOST_TG_TRAILING_GUTTER_PX}px))`;

/** Public-web fullscreen headers hug the page edges. */
export const WEB_FULLSCREEN_HEADER_PADDING_LEFT =
  'max(8px, env(safe-area-inset-left, 0px))';

export const WEB_FULLSCREEN_HEADER_PADDING_RIGHT =
  'max(8px, env(safe-area-inset-right, 0px))';

/** Apply to custom fullscreen top bars that are not MuiDialogTitle */
export const hostBackSafeHeaderSx: SxProps<Theme> = {
  pl: HOST_BACK_SAFE_PADDING_LEFT,
  pr: HOST_BACK_SAFE_PADDING_RIGHT,
};

/**
 * In-app Back is the arrow only — no FAB plate, ring, glow, or blur.
 * Keep a ~44px transparent hit target at the call site.
 * Typed as a plain object so callers can spread it into a single `sx`.
 */
export const arrowOnlyBackButtonSx = {
  bgcolor: 'transparent',
  backgroundColor: 'transparent',
  backgroundImage: 'none',
  border: 'none',
  boxShadow: 'none',
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
  '&:hover': {
    bgcolor: 'transparent',
    backgroundColor: 'transparent',
  },
} as const;

/** Ghost-click window after Back unmounts and the hamburger remounts in the same corner. */
export const BACK_CLICK_SWALLOW_MS = 480;
const BACK_CLICK_SWALLOW_PAD_PX = 28;

function eventClientPoint(event: Event, fallbackX: number, fallbackY: number): { x: number; y: number } {
  if ('clientX' in event && typeof (event as MouseEvent).clientX === 'number') {
    return { x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY };
  }
  const touch = (event as TouchEvent).changedTouches?.[0];
  if (touch) return { x: touch.clientX, y: touch.clientY };
  return { x: fallbackX, y: fallbackY };
}

/** Drop the delayed click/pointerup that would otherwise hit the hamburger after Back. */
export function swallowClicksNearPoint(x: number, y: number, ms = BACK_CLICK_SWALLOW_MS): void {
  if (typeof document === 'undefined') return;
  const until = Date.now() + ms;
  const onEvt = (event: Event) => {
    if (Date.now() > until) return;
    const point = eventClientPoint(event, x, y);
    if (Math.abs(point.x - x) > BACK_CLICK_SWALLOW_PAD_PX || Math.abs(point.y - y) > BACK_CLICK_SWALLOW_PAD_PX) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };
  const types: Array<keyof DocumentEventMap> = ['click', 'pointerup', 'mouseup', 'touchend'];
  types.forEach((type) => document.addEventListener(type, onEvt, true));
  window.setTimeout(() => {
    types.forEach((type) => document.removeEventListener(type, onEvt, true));
  }, ms + 40);
}

/**
 * 3-column fullscreen title: equal side gutters so the page title sits
 * in the true center and never sits under the shell Back or trailing actions.
 */
export const hostCenteredDialogTitleSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(72px, 1fr) auto minmax(72px, 1fr)',
    sm: 'minmax(80px, 1fr) auto minmax(80px, 1fr)',
  },
  alignItems: 'center',
  columnGap: 1,
};
